import { Fragment, useState, useEffect, useMemo, useCallback } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { convertAvailabilityFromGMT, getUserTimezone } from '../utils/timezoneUtils'
import { useAuth } from '../hooks/useAuth'
import { useUserProfile } from '../hooks/useUserProfile'
import { Professional as ProfessionalType } from '../lib/professionalService'
import { getProfessionalBookedMeetings } from '../lib/meetingService'
import { createRescheduleRequest } from '../lib/rescheduleService'
import { Timestamp } from 'firebase/firestore'

interface TimeSlot {
  start: string
  end: string
}

interface DayAvailability {
  enabled: boolean
  timeSlots: TimeSlot[]
}

interface WeeklyAvailability {
  monday: DayAvailability
  tuesday: DayAvailability
  wednesday: DayAvailability
  thursday: DayAvailability
  friday: DayAvailability
  saturday: DayAvailability
  sunday: DayAvailability
}

interface RescheduleCalendarProps {
  open: boolean
  onClose: () => void
  professional: ProfessionalType | null
  meetingId: string
  fixedDuration: number
  onRescheduleRequested: () => void
  onError?: (message: string) => void
}

const DAY_KEYS: (keyof WeeklyAvailability)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DOW_TO_KEY: Record<number, keyof WeeklyAvailability> = {
  0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday',
  4: 'thursday', 5: 'friday', 6: 'saturday'
}

// ─── Helpers ───
function to12h(h: number, m: number): string {
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function fmtDayLong(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

function fmtDateShort(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

type SlotPeriod = 'all' | 'morning' | 'afternoon' | 'evening'

function periodOf(h: number): 'morning' | 'afternoon' | 'evening' {
  if (h < 12) return 'morning'
  if (h < 17) return 'afternoon'
  return 'evening'
}

function getTzAbbreviation(tz: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
    const parts = formatter.formatToParts(now)
    return parts.find(p => p.type === 'timeZoneName')?.value || ''
  } catch {
    return ''
  }
}

function getTzDisplay(tz: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
    const parts = formatter.formatToParts(now)
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || ''
    return `${tz.replace(/_/g, ' ')} · ${offset}`
  } catch {
    return tz
  }
}

function formatDurationLabel(minutes: number): string {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return `${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`
  }
  return `${minutes} min`
}

// ─── Component ───
export default function RescheduleCalendar({
  open,
  onClose,
  professional,
  meetingId,
  fixedDuration,
  onRescheduleRequested,
  onError
}: RescheduleCalendarProps) {
  const { user: authUser } = useAuth()
  const { userProfile } = useUserProfile()
  const userTimezone = getUserTimezone(userProfile?.timezone)

  // ── State ──
  const [step, setStep] = useState(1)
  const [currentMonth, setCurrentMonth] = useState<Date>(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [slotPeriod, setSlotPeriod] = useState<SlotPeriod>('all')
  const [clientAvailability, setClientAvailability] = useState<WeeklyAvailability | null>(null)
  const [bookedMeetings, setBookedMeetings] = useState<{ startTime: Date; endTime: Date; meetingId: string }[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // ── Effects ──
  useEffect(() => {
    if (professional?.availability) {
      const converted = convertAvailabilityFromGMT(professional.availability, userTimezone)
      setClientAvailability(converted)
    } else {
      setClientAvailability(null)
    }
  }, [professional?.availability, userTimezone])

  useEffect(() => {
    const fetchBookedMeetings = async () => {
      if (!professional?.uid) return
      const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
      const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0, 23, 59, 59)
      try {
        const meetings = await getProfessionalBookedMeetings(professional.uid, start, end)
        setBookedMeetings(meetings)
      } catch (error) {
        console.error('Error fetching booked meetings:', error)
        setBookedMeetings([])
      }
    }
    if (open && professional?.uid) {
      fetchBookedMeetings()
    }
  }, [open, professional?.uid, currentMonth])

  useEffect(() => {
    if (open) {
      setStep(1)
      setCurrentMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
      setSelectedDate(null)
      setSelectedTime(null)
      setSlotPeriod('all')
      setIsSubmitting(false)
    }
  }, [open])

  // ── Availability logic ──
  const workingDays = useMemo<Set<number>>(() => {
    const result = new Set<number>()
    if (!clientAvailability) return result
    const dayNumMap: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    }
    DAY_KEYS.forEach(key => {
      const day = clientAvailability[key]
      if (day?.enabled && day.timeSlots?.length > 0) {
        result.add(dayNumMap[key])
      }
    })
    return result
  }, [clientAvailability])

  const hasBookingConflict = useCallback((date: Date, hour: number, minute: number): boolean => {
    if (bookedMeetings.length === 0) return false
    const slotStart = new Date(date)
    slotStart.setHours(hour, minute, 0, 0)
    const slotEnd = new Date(slotStart.getTime() + fixedDuration * 60 * 1000)

    return bookedMeetings.some(meeting => {
      // Skip the meeting being rescheduled (its old slot is fine to overlap with)
      if (meeting.meetingId === meetingId) return false
      const meetingStartInClientTZ = new Date(meeting.startTime.toLocaleString('sv-SE', { timeZone: userTimezone }))
      const meetingEndInClientTZ = new Date(meeting.endTime.toLocaleString('sv-SE', { timeZone: userTimezone }))
      return slotStart < meetingEndInClientTZ && slotEnd > meetingStartInClientTZ
    })
  }, [bookedMeetings, userTimezone, fixedDuration, meetingId])

  // Generate available time slots for a given date (using fixedDuration increments)
  const slotsForDate = useCallback((date: Date): { label: string; h: number; m: number }[] => {
    if (!clientAvailability) return []
    const dayKey = DOW_TO_KEY[date.getDay()]
    const dayData = clientAvailability[dayKey]
    if (!dayData?.enabled || !dayData.timeSlots?.length) return []

    const now = new Date()
    const isToday = sameDay(date, now)
    const slots: { label: string; h: number; m: number }[] = []

    dayData.timeSlots.forEach((ts: TimeSlot) => {
      const [startH, startM] = ts.start.split(':').map(Number)
      const [endH, endM] = ts.end.split(':').map(Number)
      const startMins = startH * 60 + startM
      const endMins = endH * 60 + endM

      for (let mins = startMins; mins + fixedDuration <= endMins; mins += 30) {
        const h = Math.floor(mins / 60)
        const m = mins % 60

        if (isToday && (h < now.getHours() || (h === now.getHours() && m <= now.getMinutes()))) continue
        if (hasBookingConflict(date, h, m)) continue

        slots.push({ label: to12h(h, m), h, m })
      }
    })

    return slots
  }, [clientAvailability, hasBookingConflict, fixedDuration])

  // ── Calendar grid ──
  const calendarCells = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstOfMonth = new Date(year, month, 1)
    const startDay = (firstOfMonth.getDay() + 6) % 7
    const gridStart = new Date(firstOfMonth)
    gridStart.setDate(firstOfMonth.getDate() - startDay)

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const cells: { date: Date; inMonth: boolean; isOff: boolean; isAvailable: boolean; isToday: boolean }[] = []
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart)
      d.setDate(gridStart.getDate() + i)
      const inMonth = d.getMonth() === month
      const isPast = d < today
      const isWorkDay = workingDays.has(d.getDay())
      const isToday_ = sameDay(d, today)

      cells.push({
        date: d,
        inMonth,
        isOff: !inMonth || isPast || !isWorkDay,
        isAvailable: inMonth && !isPast && isWorkDay,
        isToday: isToday_
      })
    }
    return cells
  }, [currentMonth, workingDays])

  const currentSlots = useMemo(() => {
    if (!selectedDate) return []
    return slotsForDate(selectedDate)
  }, [selectedDate, slotsForDate])

  const filteredSlots = useMemo(() => {
    if (slotPeriod === 'all') return currentSlots
    return currentSlots.filter(s => periodOf(s.h) === slotPeriod)
  }, [currentSlots, slotPeriod])

  // ── Handlers ──
  const handleDayClick = (cell: typeof calendarCells[0]) => {
    if (cell.isOff) return
    setSelectedDate(cell.date)
    setSelectedTime(null)
    setSlotPeriod('all')
  }

  const handleSlotClick = (label: string) => {
    setSelectedTime(label)
  }

  const handleContinueStep1 = () => {
    if (selectedDate && selectedTime) setStep(2)
  }

  const handleRescheduleConfirm = async () => {
    if (!selectedSlotDate || !authUser || !meetingId) return

    setIsSubmitting(true)
    try {
      const newStartTime = Timestamp.fromDate(selectedSlotDate)
      const result = await createRescheduleRequest(meetingId, newStartTime)

      if (result.success) {
        onClose()
        onRescheduleRequested()
      } else {
        const errorMsg = result.error || 'Failed to create reschedule request'
        if (onError) onError(errorMsg)
      }
    } catch (error) {
      console.error('Error creating reschedule request:', error)
      const errorMsg = 'An error occurred while creating the reschedule request: ' + (error instanceof Error ? error.message : String(error))
      if (onError) onError(errorMsg)
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Derived display values ──
  const tzAbbr = getTzAbbreviation(userTimezone)
  const tzDisplay = getTzDisplay(userTimezone)
  const ready = !!selectedDate && !!selectedTime

  // Parse selected time into a Date for the reschedule request
  const selectedSlotDate = useMemo(() => {
    if (!selectedDate || !selectedTime) return null
    const [time, period] = selectedTime.split(' ')
    const [hours, minutes] = time.split(':').map(Number)
    let hour24 = hours
    if (period === 'PM' && hours !== 12) hour24 += 12
    else if (period === 'AM' && hours === 12) hour24 = 0

    const d = new Date(selectedDate)
    d.setHours(hour24, minutes, 0, 0)
    return d
  }, [selectedDate, selectedTime])

  if (!professional) return null

  const specLabel = (professional.specialization || professional.title || 'Engineer')
    .replace(' Engineer', '').replace(' Developer', '')

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {
        if (!isSubmitting) onClose()
      }}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-6 sm:py-10">
          <div className="flex min-h-full items-start justify-center">
            <Transition.Child as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 scale-[0.985]" enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-[1000px] overflow-hidden rounded-2xl md:rounded-[20px] relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 md:top-[18px] md:right-[18px] w-[34px] h-[34px] rounded-full inline-flex items-center justify-center z-10 cursor-pointer transition-colors"
                  style={{ background: 'rgba(0,0,0,0.45)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16)', backdropFilter: 'blur(6px)' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                {/* ── Header ── */}
                <div className="px-6 md:px-8 pt-6 pb-4 flex items-center justify-between gap-6"
                  style={{ borderBottom: '1px solid hsl(0 0% 20% / 0.6)' }}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-xl grid place-items-center text-[14px] font-semibold shrink-0 overflow-hidden"
                      style={{ background: 'linear-gradient(135deg, hsl(0 0% 28%), hsl(0 0% 9%))', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                      {professional.profileImage ? (
                        <img src={professional.profileImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        professional.displayName.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[17px] font-semibold tracking-tight truncate">Reschedule with {professional.displayName}</div>
                      <div className="text-white/50 text-[12.5px] truncate">
                        {specLabel}{professional.company && <> · <span className="text-white/85">{professional.company}</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-2 text-[11.5px] text-white/50">
                    <span>{formatDurationLabel(fixedDuration)}</span>
                    <span className="opacity-40">·</span>
                    <span>Video call</span>
                  </div>
                </div>

                {/* ── Stepper ── */}
                <div className="px-6 md:px-8 py-3 flex items-center gap-4 sm:gap-6 text-[12px] overflow-x-auto"
                  style={{ borderBottom: '1px solid hsl(0 0% 20% / 0.5)', background: 'rgba(255,255,255,0.015)' }}>
                  {[
                    { n: 1, label: 'Pick a new time' },
                    { n: 2, label: 'Confirm reschedule' },
                  ].map(({ n, label }, i) => (
                    <Fragment key={n}>
                      {i > 0 && <span className="text-white/25">→</span>}
                      <span className={`flex items-center gap-2 ${step === n ? 'text-white' : step > n ? 'text-white' : 'text-white/50'}`}>
                        <span className={`step-num-pill ${step === n ? 'step-num-pill-active' : ''} ${step > n ? 'step-num-pill-done' : ''}`}>
                          {step > n ? (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
                          ) : n}
                        </span>
                        <span className="font-medium whitespace-nowrap">{label}</span>
                      </span>
                    </Fragment>
                  ))}
                </div>

                {/* ═══════ STEP 1: Pick a new time ═══════ */}
                {step === 1 && (
                  <div className="grid md:grid-cols-[1fr_320px]">
                    {/* Calendar + slots */}
                    <div className="p-6 md:p-8 min-w-0">
                      {/* Month nav */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <div className="text-[18px] font-medium tracking-tight">{fmtMonthYear(currentMonth)}</div>
                          <div className="text-white/50 text-[11.5px] mt-0.5">{tzDisplay}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button className="btn btn-ghost !px-2.5 !py-2" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
                          </button>
                          <button className="btn btn-ghost !px-2.5 !py-2" onClick={() => setCurrentMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>Today</button>
                          <button className="btn btn-ghost !px-2.5 !py-2" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                          </button>
                        </div>
                      </div>

                      {/* Calendar grid */}
                      <div>
                        <div className="cal-grid">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                            <div key={d} className="cal-dow">{d}</div>
                          ))}
                        </div>
                        <div className="cal-grid">
                          {calendarCells.map((cell, i) => {
                            const isSelected = selectedDate && sameDay(cell.date, selectedDate)
                            let cls = 'cal-day'
                            if (!cell.inMonth) cls += ' cal-day-other'
                            else if (cell.isOff) cls += ' cal-day-off'
                            else if (isSelected) cls += ' cal-day-selected'
                            else cls += ' cal-day-available'
                            if (cell.isToday && !isSelected) cls += ' cal-day-today'

                            return (
                              <div
                                key={i}
                                className={cls}
                                onClick={() => handleDayClick(cell)}
                              >
                                <span>{cell.date.getDate()}</span>
                                {(cell.isAvailable || isSelected) && <span className="cal-dot" />}
                              </div>
                            )
                          })}
                        </div>
                      </div>

                      {/* Day legend */}
                      <div className="flex items-center gap-5 mt-5 text-[11px] text-white/50">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-white" /> Has availability
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }} /> Selectable
                        </span>
                      </div>

                      {/* Slot list */}
                      <div className="mt-7 pt-6" style={{ borderTop: '1px solid hsl(0 0% 20% / 0.5)' }}>
                        <div className="flex items-start sm:items-center justify-between mb-4 flex-col sm:flex-row gap-3">
                          <div>
                            <div className="text-[14px] font-medium">
                              {selectedDate ? fmtDayLong(selectedDate) : 'Pick a day to see times'}
                            </div>
                            {selectedDate && (
                              <div className="text-white/50 text-[11.5px] mt-0.5">
                                {currentSlots.length > 0
                                  ? `${currentSlots.length} sessions available · ${formatDurationLabel(fixedDuration)} each`
                                  : 'No availability this day'}
                              </div>
                            )}
                          </div>
                          {selectedDate && currentSlots.length > 0 && (
                            <div className="flex items-center gap-1.5 text-[11.5px]">
                              {(['all', 'morning', 'afternoon', 'evening'] as SlotPeriod[]).map(p => (
                                <button
                                  key={p}
                                  className={`filter-chip !px-2.5 !py-1 !text-[11px] ${slotPeriod === p ? 'filter-chip-active' : ''}`}
                                  onClick={() => setSlotPeriod(p)}
                                >
                                  {p.charAt(0).toUpperCase() + p.slice(1)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {selectedDate && filteredSlots.length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                            {filteredSlots.map(s => (
                              <button
                                key={s.label}
                                className={`book-slot ${selectedTime === s.label ? 'book-slot-selected' : ''}`}
                                onClick={() => handleSlotClick(s.label)}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        )}

                        {selectedDate && filteredSlots.length === 0 && currentSlots.length > 0 && (
                          <div className="text-center py-10 text-white/50 text-[12.5px]">
                            No times match that filter. Try another period or another day.
                          </div>
                        )}

                        {selectedDate && currentSlots.length === 0 && (
                          <div className="text-center py-10 text-white/50 text-[12.5px]">
                            No available time slots for this day.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Side summary */}
                    <div className="p-6 md:p-8 flex flex-col gap-5"
                      style={{ background: 'rgba(255,255,255,0.015)', borderLeft: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <div className="text-[11px] tracking-widest uppercase text-white/50">Reschedule details</div>

                      <div>
                        <div className="summary-row">
                          <span className="summary-lbl">SESSION</span>
                          <span>1:1 video call · {formatDurationLabel(fixedDuration)}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-lbl">WITH</span>
                          <span className="font-medium">{professional.displayName}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-lbl">NEW DATE</span>
                          <span className={selectedDate ? '' : 'text-white/50 italic'}>
                            {selectedDate ? fmtDayLong(selectedDate) : 'Choose a day'}
                          </span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-lbl">NEW TIME</span>
                          <span className={selectedTime ? '' : 'text-white/50 italic'}>
                            {selectedTime ? `${selectedTime} ${tzAbbr}` : 'Pick a slot'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-auto pt-3">
                        <button
                          className="btn btn-primary w-full justify-center !py-3"
                          disabled={!ready}
                          style={{ opacity: ready ? 1 : 0.45, cursor: ready ? 'pointer' : 'not-allowed' }}
                          onClick={handleContinueStep1}
                        >
                          Continue
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                        </button>
                        <p className="text-white/50 text-[11px] mt-3 leading-relaxed">
                          The other participant will need to approve before the time is changed.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ═══════ STEP 2: Confirm reschedule ═══════ */}
                {step === 2 && (
                  <div className="grid md:grid-cols-[1fr_320px]">
                    <div className="p-6 md:p-8 flex flex-col gap-5">
                      <div>
                        <h3 className="text-[18px] font-medium tracking-tight">Confirm reschedule</h3>
                        <p className="text-white/50 text-[12.5px] mt-1.5 max-w-md">
                          This will send a reschedule request to {professional.displayName}. They'll need to approve the new time before the meeting is moved.
                        </p>
                      </div>

                      {/* Reschedule summary card */}
                      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                        <div className="flex items-center gap-4 mb-4 pb-4" style={{ borderBottom: '1px solid hsl(0 0% 20% / 0.4)' }}>
                          <div className="w-10 h-10 rounded-xl grid place-items-center text-[12px] font-semibold shrink-0 overflow-hidden"
                            style={{ background: 'linear-gradient(135deg, hsl(0 0% 28%), hsl(0 0% 9%))', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                            {professional.profileImage ? (
                              <img src={professional.profileImage} alt="" className="w-full h-full object-cover" />
                            ) : (
                              professional.displayName.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()
                            )}
                          </div>
                          <div>
                            <div className="text-[14px] font-medium">{professional.displayName}</div>
                            <div className="text-white/50 text-[12px]">{specLabel}</div>
                          </div>
                        </div>

                        <div className="space-y-3 text-[13px]">
                          <div className="flex items-center gap-3">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="text-white/50 shrink-0"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
                            <span>{selectedDate ? fmtDateShort(selectedDate) : ''}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="text-white/50 shrink-0"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                            <span>{selectedTime} {tzAbbr} · {formatDurationLabel(fixedDuration)}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="text-white/50 shrink-0"><path d="M23 7l-7 5 7 5z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                            <span>Wonderstood video</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5 text-[12px] text-white/50 mt-1" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)', borderRadius: '12px', padding: '12px 14px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-white/60 shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                        <span>The meeting time won't change until the other participant approves. You'll be notified of their response.</span>
                      </div>
                    </div>

                    {/* Side summary */}
                    <div className="p-6 md:p-8 flex flex-col gap-5"
                      style={{ background: 'rgba(255,255,255,0.015)', borderLeft: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] tracking-widest uppercase text-white/50">Reschedule details</div>
                        <button className="btn btn-ghost !px-2 !py-1 text-[11px]" onClick={() => setStep(1)}>Edit</button>
                      </div>

                      <div>
                        <div className="summary-row"><span className="summary-lbl">WITH</span><span className="font-medium">{professional.displayName}</span></div>
                        <div className="summary-row"><span className="summary-lbl">SESSION</span><span>1:1 video call · {formatDurationLabel(fixedDuration)}</span></div>
                        <div className="summary-row"><span className="summary-lbl">NEW DATE</span><span>{selectedDate ? fmtDayLong(selectedDate) : ''}</span></div>
                        <div className="summary-row"><span className="summary-lbl">NEW TIME</span><span>{selectedTime} {tzAbbr}</span></div>
                      </div>

                      <div className="mt-auto flex flex-col gap-2">
                        <button
                          className="btn btn-primary w-full justify-center !py-3"
                          onClick={handleRescheduleConfirm}
                          disabled={isSubmitting}
                          style={{ opacity: isSubmitting ? 0.45 : 1 }}
                        >
                          {isSubmitting ? (
                            <span className="flex items-center gap-2">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                              Sending...
                            </span>
                          ) : (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
                              Send reschedule request
                            </>
                          )}
                        </button>
                        <button className="btn btn-ghost w-full justify-center" onClick={() => setStep(1)}>Back</button>
                      </div>
                    </div>
                  </div>
                )}
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
